import React from 'react';
import { AlertTriangle, Check, CheckCircle2, Circle, Folder, FolderOpen, RotateCcw, Satellite, Trash2, Workflow, X, XCircle } from 'lucide-react';
import { C } from '../theme.js';
import { Alert, Button, Code, Eyebrow, FieldLabel, IconButton, Select, TextInput } from '../components/Primitives.jsx';
import { StepIcon } from '../components/StepIcon.jsx';
import { CATEGORIES, FILENAME_TOKENS, GATEABLE_STEPS, OPERATORS, PHASE2_LABEL, PHASE2_TOOLTIP, SOURCE_COLUMNS, STEP_TYPES, outputNameIssue, resolveFilenamePattern, stepAccent, summarizeStep } from '../data.js';
import { mapCodeStats, warningLabel } from './transform/codeMode/statsMapping.js';
import { INSPECTORS, PlaceholderInspector } from './StepInspectorsExtra.jsx';
import { ApplyWhenSection } from './shared/ApplyWhenSection.jsx';
import { ConditionRows } from './shared/ConditionRows.jsx';
import { OutputNameField } from './shared/OutputNameField.jsx';
import { SOURCE_ANCHOR_RESOLVERS } from './shared/sourceAnchor.js';
// Shared primitives live here to avoid a circular dependency with StepInspectorsExtra.jsx
import { ColumnsContext as _ColumnsContext, OriginalColumnsContext as _OriginalColumnsContext, ColumnChips as _ColumnChips, Segmented as _Segmented } from './InspectorPrimitives.jsx';

// Re-export so existing consumers (StepInspectorsExtra, tests, etc.) keep working
// without needing their own import-path updates.
export const ColumnsContext = _ColumnsContext;
export const OriginalColumnsContext = _OriginalColumnsContext;
export const ColumnChips = _ColumnChips;
export const Segmented = _Segmented;

/* eslint-disable */
// ─── Right-side inspector panels: per-step config + data preview drawer ──


// ── Source step inspector ───────────────────────────────────────────────
function SourceInspector({ step, pipeline, onReplaceInput, columns }) {
  const inputFile = pipeline.inputFile || {};
  const format = (inputFile.format || 'csv').toLowerCase();
  const badge = format === 'xls'
    ? { bg: 'var(--warn-bg)', bd: 'var(--warn-border)', fg: C.warn, label: 'XLS' }
    : format === 'xlsx'
      ? { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok, label: 'XLSX' }
      : { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok, label: 'CSV' };

  if (inputFile.chained) {
    return (
      <div>
        <Eyebrow>Input · Chained from pipeline</Eyebrow>
        <div style={{
          background: C.input, border: `1px solid ${C.accentMuted}`,
          borderRadius: 8, padding: '12px 14px', marginTop: 8,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 38, height: 46, borderRadius: 4,
            background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: C.accentBright, fontSize: '1.1rem',
            flexShrink: 0,
          }}><Workflow size={18} /></div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              color: C.fg1, fontSize: '.82rem', fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{inputFile.sourcePipelineName}</div>
            <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 2 }}>
              {inputFile.sourceProjectName}
              {inputFile.sourceLastRun && inputFile.sourceLastRun !== '—'
                ? <> · last run {inputFile.sourceLastRun}</>
                : null}
            </div>
          </div>
          <Button kind="ghost" size="sm" icon={<FolderOpen size={14} />} onClick={() => onReplaceInput?.()}>Change…</Button>
        </div>

        <div style={{
          marginTop: 10, padding: '10px 12px',
          background: C.surface, border: `1px solid ${C.borderSubtle}`,
          borderRadius: 8,
        }}>
          <div style={{
            color: C.fg5, fontSize: '.66rem',
            textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
            marginBottom: 4,
          }}>Reads from</div>
          <div style={{
            color: C.fg2, fontSize: '.78rem',
            fontFamily: "'JetBrains Mono', monospace",
            wordBreak: 'break-all',
          }}>
            <span style={{ color: C.fg5 }}>
              {(inputFile.sourceOutputFolder || '').replace(/\/$/, '') + '/'}
            </span>
            <span style={{ color: C.accentBright }}>{inputFile.name}</span>
          </div>
          <div style={{ color: C.fg5, fontSize: '.7rem', marginTop: 5 }}>
            Resolved from pattern <Code>{inputFile.sourceOutputPattern}</Code>.
            This pipeline runs after the upstream pipeline writes its output.
          </div>
        </div>

        <FieldLabel>Detected schema</FieldLabel>
        <div style={{ color: C.fg5, fontSize: '.74rem', margin: '0 0 8px', lineHeight: 1.5 }}>
          Schema is inherited from the upstream pipeline's output. If the
          upstream pipeline changes its output columns, this source step's
          schema updates on next run.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {columns.map(c => (
            <span key={c.name} style={{
              background: C.input, border: `1px solid ${C.borderStrong}`,
              color: C.fg3, fontSize: '.74rem',
              padding: '4px 9px', borderRadius: 6,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{c.name}</span>
              <span style={{
                fontSize: '.62rem', color: C.accentBright, textTransform: 'uppercase',
                letterSpacing: '.06em', fontWeight: 700,
              }}>{c.type}</span>
            </span>
          ))}
        </div>
      </div>
    );
  }

  if (inputFile.dynamic) {
    return (
      <div>
        <Eyebrow>Input · Folder watcher</Eyebrow>
        <div style={{
          background: C.input, border: `1px solid ${C.accentMuted}`,
          borderRadius: 8, padding: '12px 14px', marginTop: 8,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            width: 38, height: 46, borderRadius: 4,
            background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: C.accentBright, fontSize: '1.05rem',
            flexShrink: 0,
          }}><Satellite size={17} /></div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              color: C.fg1, fontSize: '.82rem', fontWeight: 600,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              {inputFile.watchPath}
              <span style={{ color: C.accentBright }}>
                {inputFile.watchPattern || '*'}
              </span>
            </div>
            <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 2 }}>
              {inputFile.matchCount
                ? `${inputFile.matchCount} file${inputFile.matchCount === 1 ? '' : 's'} match`
                : 'Waiting for new files'}
              {inputFile.watchPattern
                ? <> · pattern <Code>{inputFile.watchPattern}</Code></>
                : ' · any filename, oldest first'}
            </div>
          </div>
          <Button kind="ghost" size="sm" icon={<FolderOpen size={14} />} onClick={() => onReplaceInput?.()}>Change…</Button>
        </div>

        {inputFile.nextUp && (
          <div style={{
            marginTop: 10, padding: '10px 12px',
            background: C.surface, border: `1px solid ${C.borderSubtle}`,
            borderRadius: 8,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{
              background: C.input, border: `1px solid ${C.accentMuted}`,
              color: C.accentBright,
              fontSize: '.6rem', fontWeight: 700,
              textTransform: 'uppercase', letterSpacing: '.08em',
              padding: '2px 7px', borderRadius: 4,
              flexShrink: 0,
            }}>Next up</span>
            <span style={{
              color: C.fg2, fontSize: '.78rem',
              fontFamily: "'JetBrains Mono', monospace",
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              minWidth: 0, flex: 1,
            }}>{inputFile.nextUp}</span>
            <span style={{
              color: C.fg5, fontSize: '.72rem',
              fontFamily: "'JetBrains Mono', monospace",
            }}>{inputFile.size} · {(inputFile.rows || 0).toLocaleString()} rows</span>
          </div>
        )}

        <FieldLabel>Detected schema</FieldLabel>
        <div style={{ color: C.fg5, fontSize: '.74rem', margin: '0 0 8px', lineHeight: 1.5 }}>
          Schema is locked to the columns below. Files in the watch folder must
          match this shape — extra columns are ignored, missing columns fail
          validation.
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {columns.map(c => (
            <span key={c.name} style={{
              background: C.input, border: `1px solid ${C.borderStrong}`,
              color: C.fg3, fontSize: '.74rem',
              padding: '4px 9px', borderRadius: 6,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{c.name}</span>
              <span style={{
                fontSize: '.62rem', color: C.accentBright, textTransform: 'uppercase',
                letterSpacing: '.06em', fontWeight: 700,
              }}>{c.type}</span>
            </span>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <Eyebrow>Input file</Eyebrow>
      <div style={{
        background: C.input, border: `1px solid ${C.borderStrong}`,
        borderRadius: 8, padding: '12px 14px', marginTop: 8,
        display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <div style={{
          width: 38, height: 46, borderRadius: 4,
          background: badge.bg, border: `1px solid ${badge.bd}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: badge.fg, fontSize: '.62rem', fontWeight: 700, letterSpacing: '.04em',
          flexShrink: 0,
        }}>{badge.label}</div>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{
            color: C.fg1, fontSize: '.82rem', fontWeight: 600,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            fontFamily: "'JetBrains Mono', monospace",
          }}>{inputFile.name}</div>
          <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 2 }}>
            {inputFile.size} · {(inputFile.rows || 0).toLocaleString()} rows · {columns.length} columns
          </div>
        </div>
        <Button kind="ghost" size="sm" icon={<FolderOpen size={14} />} onClick={() => onReplaceInput?.()}>Replace…</Button>
      </div>

      <FieldLabel>Detected schema</FieldLabel>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {columns.map(c => (
          <span key={c.name} style={{
            background: C.input, border: `1px solid ${C.borderStrong}`,
            color: C.fg3, fontSize: '.74rem',
            padding: '4px 9px', borderRadius: 6,
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{c.name}</span>
            <span style={{
              fontSize: '.62rem', color: C.accentBright, textTransform: 'uppercase',
              letterSpacing: '.06em', fontWeight: 700,
            }}>{c.type}</span>
          </span>
        ))}
      </div>

      <FieldLabel>Sample rows</FieldLabel>
      <div style={{ color: C.fg4, fontSize: '.78rem' }}>
        First 24 rows previewed in panel below. CSV detected with header row,
        UTF-8 encoding, comma delimiter.
      </div>
    </div>
  );
}

// ── Dedupe inspector ────────────────────────────────────────────────────
function DedupeInspector({ step, onChange, columns }) {
  const cols = step.config.columns || [];
  const toggle = (name) => {
    const next = cols.includes(name) ? cols.filter(c => c !== name) : [...cols, name];
    onChange({ ...step, config: { ...step.config, columns: next } });
  };
  return (
    <div>
      <Eyebrow>Match rows on these columns</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Rows where the chosen columns produce the same lower-cased trimmed value
        are collapsed to the first occurrence.
      </div>
      <ColumnChips all={columns} selected={cols} onToggle={toggle} />

      {cols.length === 0 ? (
        <Alert kind="warn" style={{ marginTop: 12 }}>
          Pick at least one column. With no columns selected this step is a no-op.
        </Alert>
      ) : (
        <Alert kind="info" style={{ marginTop: 12 }}>
          Composite key: <Code>{cols.map(c => `{${c}}`).join(' + ')}</Code>
        </Alert>
      )}
    </div>
  );
}

// ── Filter inspector ────────────────────────────────────────────────────
// Same Source-anchored, self-preserving contract as Unpivot/Group By/
// Compute: Source unset/"__self__" means self (zero behavior change for
// every existing pipeline — no saved Filter config has a `source` key
// before this). When Source isn't self, execute_pipeline (executor.py)
// reverts next_rows back to the unchanged self payload after this step, so
// the filtered result is only reachable through Output Name.
function FilterInspector({ step, onChange, columns, pipeline, secondaryDatasets, sheets, onBrowseJoinFile, runMap }) {
  const c = step.config || {};
  const conditions = c.conditions || [];
  const [tab, setTab] = React.useState('visual');

  // Always self (the previous step's output) — Filter has no Source of its
  // own to pick.
  const cols = columns || [];
  const outputNameIssueRes = outputNameIssue(c.output_name, { sheets, secondaryDatasets, steps: pipeline?.steps, stepId: step.id });

  const update = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const updateCond = (i, patch) => {
    const next = conditions.map((cd, j) => j === i ? { ...cd, ...patch } : cd);
    update({ conditions: next });
  };
  const addCond = () => update({ conditions: [...conditions, { column: (cols[0]?.name || ''), op: 'eq', value: '' }] });
  const removeCond = (i) => update({ conditions: conditions.filter((_, j) => j !== i) });

  // Compose pseudo-formula
  const formula = conditions.map(cd => {
    const op = OPERATORS.find(o => o.id === cd.op);
    if (cd.op === 'in_source' || cd.op === 'nin_source') return `${cd.column} ${op.label} ${cd.source || '?'}.${cd.source_column || '?'}`;
    if (!op?.needsValue) return `${cd.column} ${op?.label || cd.op}`;
    if (cd.op === 'between' || cd.op === 'nbetween') {
      const b = (x) => (cd.value_mode === 'column' ? `[${x}]` : (isNaN(Number(x)) ? `"${x}"` : x));
      return `${cd.column} ${op.label} ${b(cd.value)} and ${b(cd.value2)}`;
    }
    let v;
    if (cd.value_mode === 'column' && cd.op !== 'matches' && cd.op !== 'in' && cd.op !== 'nin') v = `[${cd.value}]`;
    else if (cd.op === 'in' || cd.op === 'nin') v = `(${String(cd.value ?? '').split(',').map(x => x.trim()).filter(Boolean).map(x => `"${x}"`).join(', ')})`;
    else v = isNaN(Number(cd.value)) ? `"${cd.value}"` : cd.value;
    const as = (cd.op === 'eq' || cd.op === 'neq') && cd.compare && cd.compare !== 'text' ? ` (as ${cd.compare})` : '';
    return `${cd.column} ${op?.label || cd.op} ${v}${as}`;
  }).join(` ${c.conjunction || 'AND'} `);

  return (
    <div>
      {/* Mode + tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        <Segmented
          value={c.mode || 'drop'}
          onChange={(v) => update({ mode: v })}
          options={[{ value: 'drop', label: 'DROP matching' }, { value: 'keep', label: 'KEEP matching' }]}
        />
      </div>

      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, marginBottom: 12 }}>
        {[['visual', 'Visual builder'], ['formula', 'Formula']].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{
              flex: 1, background: 'transparent',
              border: 'none', borderBottom: `2px solid ${tab === k ? C.accent : 'transparent'}`,
              color: tab === k ? C.fg1 : C.fg4,
              fontFamily: 'inherit', fontWeight: tab === k ? 600 : 500,
              fontSize: '.78rem', padding: '8px 6px', cursor: 'pointer',
              marginBottom: -1, transition: 'all .15s',
            }}>{l}</button>
        ))}
      </div>

      {tab === 'visual' ? (
        <>
          {conditions.length > 1 && (
            <div style={{ marginBottom: 10 }}>
              <Eyebrow style={{ marginBottom: 6 }}>Match</Eyebrow>
              <Segmented
                value={c.conjunction || 'AND'}
                onChange={(v) => update({ conjunction: v })}
                options={[{ value: 'AND', label: 'ALL · AND' }, { value: 'OR', label: 'ANY · OR' }]}
              />
            </div>
          )}

          <ConditionRows conditions={conditions} columns={cols} onUpdate={updateCond} onRemove={removeCond} variant="card"
            sourceCtx={{ secondaryDatasets, sheets, pipeline, currentStep: step, runMap, onBrowse: onBrowseJoinFile ? () => onBrowseJoinFile(step.id) : undefined, buildTargetName: pipeline?.inputFile?.buildTargetName }} />

          <Button kind="ghost" size="sm" onClick={addCond} icon={'＋'}>Add condition</Button>
        </>
      ) : (
        <>
          <FieldLabel>Pseudo-formula (read-only preview)</FieldLabel>
          <div style={{
            background: C.input, border: `1px solid ${C.borderStrong}`, borderRadius: 8,
            padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace",
            fontSize: '.78rem', color: C.accentBright, lineHeight: 1.55,
            whiteSpace: 'pre-wrap', wordBreak: 'break-word', minHeight: 56,
          }}>
            {formula ? <>
              <span style={{ color: C.fg4 }}>{c.mode === 'keep' ? 'KEEP' : 'DROP'} WHERE </span>
              {formula}
            </> : <span style={{ color: C.fg5 }}>no conditions yet — add some in the visual builder</span>}
          </div>
          <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 8, lineHeight: 1.5 }}>
            Free-text formula editing is coming in <Code>Phase 2</Code>. Until then, build conditions in the visual tab.
          </div>
        </>
      )}

      <OutputNameField
        value={c.output_name}
        onChange={(v) => update({ output_name: v })}
        required={false}
        hint="Optional — also publish this result under a name so later blocks can pick it as their Source"
        placeholder="e.g., active_only"
        issue={outputNameIssueRes}
      />
    </div>
  );
}

// ── Transform inspector ─────────────────────────────────────────────────
const TRANSFORM_MODES = [
  { id: 'trim', label: 'Trim whitespace', desc: 'Strip leading/trailing spaces' },
  { id: 'lower', label: 'Lowercase', desc: 'a → a' },
  { id: 'upper', label: 'Uppercase', desc: 'a → A' },
  { id: 'title', label: 'Title Case', desc: 'a → A' },
  { id: 'replace', label: 'Find & replace', desc: 'Literal text substitution' },
  { id: 'fill_blank', label: 'Fill blanks', desc: 'Replace blanks with a constant' },
  { id: 'set_blank', label: 'Set blank', desc: 'Clear the cell to "" unconditionally' },
  { id: 'expression', label: 'Expression', desc: 'UPPER, LOWER, CONCAT, REPLACE, LEFT, RIGHT…' },
];

function TransformInspector({ step, onChange, columns }) {
  const c = step.config || {};
  const update = (patch) => onChange({ ...step, config: { ...c, ...patch } });
  const mode = TRANSFORM_MODES.find(m => m.id === c.mode) || TRANSFORM_MODES[0];

  return (
    <div>
      <FieldLabel>Column</FieldLabel>
      <Select
        value={c.column || (columns[0]?.name || '')}
        onChange={(v) => update({ column: v })}
        options={columns.map(col => ({ value: col.name, label: col.name }))}
      />

      <FieldLabel hint={mode.desc}>Operation</FieldLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        {TRANSFORM_MODES.map(m => {
          const on = c.mode === m.id;
          return (
            <button
              key={m.id}
              onClick={() => update({ mode: m.id })}
              style={{
                fontFamily: 'inherit', cursor: 'pointer',
                background: on ? 'var(--info-bg)' : C.input,
                color: on ? C.accentBright : C.fg3,
                border: `1px solid ${on ? C.accentMuted : C.border}`,
                borderRadius: 8, padding: '8px 10px',
                fontSize: '.76rem', fontWeight: 600,
                textAlign: 'left',
                transition: 'all .15s',
              }}
            >{m.label}</button>
          );
        })}
      </div>

      {/* mode-specific inputs */}
      {c.mode === 'replace' && (
        <>
          <FieldLabel>Find</FieldLabel>
          <TextInput value={c.find} onChange={(v) => update({ find: v })} placeholder="text to find" />
          <FieldLabel>Replace with</FieldLabel>
          <TextInput value={c.replace} onChange={(v) => update({ replace: v })} placeholder="replacement" />
        </>
      )}
      {c.mode === 'fill_blank' && (
        <>
          <FieldLabel>Fill value</FieldLabel>
          <TextInput value={c.fill} onChange={(v) => update({ fill: v })} placeholder="e.g. UNKNOWN" />
        </>
      )}
      {c.mode === 'expression' && (
        <>
          <FieldLabel hint="Reference cells with {column}">Expression</FieldLabel>
          <TextInput
            mono
            value={c.expression}
            onChange={(v) => update({ expression: v })}
            placeholder={'UPPER({country})'}
          />
          <div style={{
            background: C.input, border: `1px solid ${C.border}`,
            borderRadius: 8, padding: 10, marginTop: 8,
            fontSize: '.72rem', color: C.fg4, lineHeight: 1.6,
          }}>
            <div style={{ color: C.fg2, marginBottom: 4, fontWeight: 600 }}>Functions</div>
            <Code>UPPER(x)</Code> <Code>LOWER(x)</Code> <Code>TITLE(x)</Code> <Code>TRIM(x)</Code><br />
            <Code>CONCAT(a,b,…)</Code> <Code>REPLACE(x,from,to)</Code><br />
            <Code>LEFT(x,n)</Code> <Code>RIGHT(x,n)</Code> <Code>BLANK</Code>
          </div>
        </>
      )}
    </div>
  );
}

// ── Output inspector ────────────────────────────────────────────────────

// JS mirror of server/app/engine/output.py's excel_tab_name_issue — same
// rules, kept in sync by comment cross-reference (the established pattern
// for backend-resolver JS mirrors in this codebase, e.g. sourceAnchor.js).
// Purely a live-typing UX check; the server enforces the real rule
// authoritatively at run time regardless (see output_original.py's
// append_sheet_issue), so this being out of sync would surface as a run
// error, not a silent bypass.
const EXCEL_TAB_UNSAFE_CHARS = /[[\]:*?/\\]/;
function excelTabNameIssue(name) {
  const trimmed = (name || '').trim();
  if (!trimmed) return "tab names can't be empty";
  if (name.length > 31) return 'Excel tab names are limited to 31 characters';
  if (EXCEL_TAB_UNSAFE_CHARS.test(name)) return "Excel tab names can't contain [ ] : * ? / \\";
  if (name.startsWith("'") || name.endsWith("'")) return "Excel tab names can't start or end with an apostrophe";
  if (trimmed.toLowerCase() === 'history') return "'History' is reserved by Excel";
  return null;
}

function OutputInspector({ step, pipeline, onChangeOutput, onBrowseFolder, sheets = [] }) {
  const o = pipeline.output || {};
  const filename = o.filename || '';
  const folder = o.folder || '';
  const format = (o.format || 'csv').toLowerCase();
  const mode = o.mode || 'new_per_run';
  const seqStart = o.seqStart ?? 1;
  const seqEnd = o.seqEnd ?? 9999;
  const seqNext = o.seqNext ?? seqStart;
  const usesSeq = /\{seq(?::\d+)?\}/.test(filename);
  const usesInput = /\{input_(file|name|ext)\}/.test(filename);

  // Save as: "standalone" (today's existing behavior, default, unchanged)
  // or "append_to_input" — append this run's output as a new sheet into a
  // COPY of the pipeline's stored input workbook (server/app/engine/
  // output_original.py). Output otherwise happens exactly like today:
  // written to storage/{run_id}, then auto-downloaded — Feature B's
  // folder-write/saveMode work is separate and still on hold.
  const saveAs = o.saveAs || 'standalone';
  const inputFormat = (pipeline.inputFile?.format || '').toLowerCase();
  const hasStoredInput = !!pipeline.inputFile?.storagePath;
  const canAppendToInput = hasStoredInput && inputFormat === 'xlsx';
  const appendDisabledReason = !hasStoredInput
    ? 'No stored input file for this pipeline yet — upload one first.'
    : inputFormat !== 'xlsx'
      ? `Needs an .xlsx input workbook — this pipeline's input is ${inputFormat ? `.${inputFormat}` : 'not stored'}.`
      : undefined;
  // Prefilled with the build target's own name until the user types their
  // own — ?? (not ||) so an explicitly-cleared field stays blank rather
  // than snapping back to the suggestion.
  const suggestedSheetName = pipeline.inputFile?.buildTargetName || pipeline.inputFile?.sheetName || 'Result';
  const appendSheetName = o.appendSheetName ?? suggestedSheetName;
  // Best-effort, client-side only — the real, authoritative check is
  // server-side against the actual stored workbook (output_original.py's
  // append_sheet_issue), which this run would fail against with the same
  // wording if it somehow drifted (e.g. a sheet renamed elsewhere in the
  // same edit session before saving).
  const existingWorkbookSheetNames = [
    pipeline.inputFile?.buildTargetName || pipeline.inputFile?.sheetName,
    ...sheets.keys(),
  ].filter(Boolean);
  const appendNameIssue = saveAs === 'append_to_input'
    ? (excelTabNameIssue(appendSheetName)
      || (existingWorkbookSheetNames.some(n => n.trim().toLowerCase() === appendSheetName.trim().toLowerCase())
        ? `a sheet named "${appendSheetName}" already exists in this workbook`
        : null))
    : null;

  // Patch helper — only touch the fields supplied.
  const patch = (extra) => onChangeOutput?.({ ...o, ...extra });

  const fileInputRef = React.useRef(null);
  const [cursor, setCursor] = React.useState(filename.length);
  React.useEffect(() => {
    setCursor(c => Math.min(c, filename.length));
  }, [filename]);

  // Token insertion preserves cursor position.
  const insertToken = (token) => {
    const head = filename.slice(0, cursor);
    const tail = filename.slice(cursor);
    const next = head + token + tail;
    patch({ filename: next });
    // Re-focus and re-place cursor after the inserted token.
    requestAnimationFrame(() => {
      const el = fileInputRef.current;
      if (el) {
        el.focus();
        const pos = head.length + token.length;
        el.setSelectionRange?.(pos, pos);
        setCursor(pos);
      }
    });
  };

  // Resolve a sample preview using the live input file (or watcher's next-up).
  const inputForPreview = pipeline.inputFile?.dynamic
    ? (pipeline.inputFile.nextUp || 'newfile.csv')
    : (pipeline.inputFile?.name || 'input.csv');
  const ext = '.' + format;
  const stem = (filename || '').replace(/\.[^.]+$/, '');
  const resolved = resolveFilenamePattern(stem + ext, {
    runId: 'R-026-A93',
    inputFile: inputForPreview,
    now: new Date(),
    seq: seqNext,
  });
  const fullPath = (folder ? folder.replace(/\/$/, '') + '/' : '') + resolved;

  return (
    <div>
      {/* Save as ────────────────────────────────────────────────── */}
      <FieldLabel hint="Standalone writes a fresh file, exactly like today. Append adds this run's output as a new sheet in a COPY of your uploaded workbook — the original file is never modified.">
        Save as
      </FieldLabel>
      <Segmented
        value={saveAs}
        onChange={(v) => patch({ saveAs: v, ...(v === 'append_to_input' && o.appendSheetName == null ? { appendSheetName: suggestedSheetName } : {}) })}
        options={[
          { value: 'standalone', label: 'Standalone file' },
          { value: 'append_to_input', label: 'Append to input file', disabled: !canAppendToInput, disabledReason: appendDisabledReason },
        ]}
      />
      {saveAs === 'append_to_input' ? (
        <>
          <FieldLabel hint="The name of the new tab added to your uploaded workbook. Must be unique in that workbook and follow Excel's tab-name rules.">
            Sheet name
          </FieldLabel>
          <TextInput
            value={appendSheetName}
            onChange={(v) => patch({ appendSheetName: v })}
            placeholder={suggestedSheetName}
          />
          {appendNameIssue && (
            <Alert kind="error" style={{ marginTop: 8 }}>{appendNameIssue}</Alert>
          )}
          <Alert kind="info" style={{ marginTop: 10 }}>
            Written to a <strong>copy</strong> of <Code>{pipeline.inputFile?.name || 'your input file'}</Code> — the
            original stored file is never changed. Output is always XLSX in this mode; the Filename pattern and
            Format settings below don't apply.
          </Alert>
          <Alert kind="warn" style={{ marginTop: 8 }}>
            Formula cells already in the original workbook keep their formulas, but not their last-calculated
            values — Excel recalculates them automatically when you open the file.
          </Alert>
        </>
      ) : null}

      {/* Folder ─────────────────────────────────────────────────── */}
      <FieldLabel>Output folder <span title={PHASE2_TOOLTIP} style={{ marginLeft: 6, fontSize: '.6rem', fontWeight: 700, color: C.fg5, border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 5px' }}>{PHASE2_LABEL}</span></FieldLabel>
      <div title={PHASE2_TOOLTIP} style={{ display: 'flex', gap: 6, alignItems: 'stretch', opacity: 0.45, pointerEvents: 'none' }}>
        <TextInput mono value={folder} onChange={(v) => patch({ folder: v })} placeholder="/path/to/output" />
        <Button kind="ghost" size="md" icon={<Folder size={14} />} disabled>
          Browse…
        </Button>
      </div>

      {/* Filename + Format don't apply in "Append to input file" mode —
          dimmed rather than removed, so the settings aren't lost if the
          user switches back to Standalone. */}
      <div style={saveAs === 'append_to_input' ? { opacity: 0.45, pointerEvents: 'none' } : undefined}>
      {/* Filename ───────────────────────────────────────────────── */}
      <FieldLabel hint="Tokens like {run_id}, {input_name}, {date}, {seq:0001} expand at run time">
        Filename pattern
      </FieldLabel>
      <div style={{ position: 'relative' }}>
        <input
          ref={fileInputRef}
          value={filename}
          onChange={(e) => {
            patch({ filename: e.target.value });
            setCursor(e.target.selectionStart || 0);
          }}
          onKeyUp={(e) => setCursor(e.target.selectionStart || 0)}
          onClick={(e) => setCursor(e.target.selectionStart || 0)}
          spellCheck={false}
          placeholder="customers_clean_{input_name}_{run_id}"
          style={{
            width: '100%',
            background: C.input,
            border: `1px solid ${C.borderStrong}`,
            borderRadius: 8,
            padding: '8px 11px',
            color: C.fg1,
            fontSize: '.84rem',
            fontFamily: "'JetBrains Mono', monospace",
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
        <div style={{
          position: 'absolute',
          right: 10, top: '50%', transform: 'translateY(-50%)',
          color: C.fg6, fontSize: '.7rem',
          fontFamily: "'JetBrains Mono', monospace",
          pointerEvents: 'none',
          background: C.input, padding: '1px 4px', borderRadius: 4,
        }}>{ext}</div>
      </div>

      {/* Token chips */}
      <TokenInsertPanel
        tokens={FILENAME_TOKENS}
        onInsert={insertToken}
      />

      {/* Sequence range (only when {seq} is used) */}
      {usesSeq && (
        <SequenceRangeRow
          start={seqStart} end={seqEnd} next={seqNext}
          onChange={(v) => patch(v)}
        />
      )}

      {/* Live resolved preview */}
      <FieldLabel hint={
        pipeline.inputFile?.dynamic
          ? 'Resolved against the next file the watcher will pick up'
          : 'Resolved against the current input file'
      }>Resolved preview</FieldLabel>
      <div style={{
        background: 'var(--bg-input)',
        border: `1px solid ${C.accentMuted}`,
        borderRadius: 8,
        padding: '10px 12px',
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '.82rem',
        color: C.fg1,
        wordBreak: 'break-all',
        lineHeight: 1.4,
      }}>
        <span style={{ color: C.fg5 }}>{folder ? folder.replace(/\/$/, '') + '/' : ''}</span>
        <span style={{ color: C.accentBright, fontWeight: 600 }}>{resolved}</span>
      </div>
      <div style={{
        color: C.fg5, fontSize: '.7rem', marginTop: 5,
        fontFamily: "'JetBrains Mono', monospace",
      }}>
        input → {inputForPreview}
        {usesSeq && <>  ·  seq → {String(seqNext).padStart(String(seqEnd).length, '0')}</>}
      </div>

      {/* Format ─────────────────────────────────────────────────── */}
      <FieldLabel>Format</FieldLabel>
      <div style={{ display: 'flex', gap: 6 }}>
        {[
          { id: 'csv', label: 'CSV', desc: 'Comma-separated' },
          { id: 'xlsx', label: 'XLSX', desc: 'Excel 2007+' },
          { id: 'xls', label: 'XLS', desc: PHASE2_LABEL, phase2: true },
        ].map(f => {
          const on = format === f.id;
          return (
            <button
              key={f.id}
              onClick={f.phase2 ? undefined : () => patch({ format: f.id })}
              disabled={f.phase2}
              title={f.phase2 ? PHASE2_TOOLTIP : undefined}
              style={{
                opacity: f.phase2 ? .45 : 1,
                flex: 1, padding: 10, textAlign: 'left',
                background: on ? 'var(--info-bg)' : C.input,
                border: `1px solid ${on ? C.accentMuted : C.border}`,
                borderRadius: 8, cursor: f.phase2 ? 'not-allowed' : 'pointer',
                transition: 'all .15s',
                fontFamily: 'inherit',
              }}
            >
              <div style={{
                color: on ? C.accentBright : C.fg2,
                fontSize: '.8rem', fontWeight: 700, marginBottom: 2,
              }}>{f.label}</div>
              <div style={{ color: C.fg5, fontSize: '.7rem' }}>{f.desc}</div>
            </button>
          );
        })}
      </div>
      {/* Only XLSX can hold every sheet — see server/app/engine/output.py. */}
      {format !== 'xlsx' && pipeline.inputFile?.buildTargetName && (
        <Alert kind="info" style={{ marginTop: 10 }}>
          {format.toUpperCase()} holds a single sheet, so only the build target
          (<Code>{pipeline.inputFile.buildTargetName}</Code>) is exported. Choose
          <strong> XLSX</strong> to bundle every sheet into one file.
        </Alert>
      )}
      </div>

      {/* Write mode ─────────────────────────────────────────────── */}
      <FieldLabel>Write mode</FieldLabel>
      <Segmented
        value={mode}
        onChange={(v) => patch({ mode: v })}
        options={[
          { value: 'new_per_run', label: 'New file per run' },
          { value: 'overwrite', label: `Overwrite · ${PHASE2_LABEL}`, disabled: true, disabledReason: PHASE2_TOOLTIP },
        ]}
      />

      <Alert kind="info" style={{ marginTop: 14 }}>
        {pipeline.inputFile?.dynamic
          ? <>This pipeline runs on every file the watcher picks up.
            Use <Code>{'{input_name}'}</Code> or <Code>{'{seq:0001}'}</Code> to
            keep each output unique.</>
          : <>The file is saved on the server and downloaded by your browser
            when the run finishes. Choosing an output folder is coming in Phase 2.</>}
      </Alert>
    </div>
  );
}

// Token-insertion chips, grouped by category.
function TokenInsertPanel({ tokens, onInsert }) {
  const groups = [
    { id: 'run', label: 'Run' },
    { id: 'input', label: 'Input file' },
    { id: 'date', label: 'Date / time' },
    { id: 'seq', label: 'Sequence' },
  ];
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{
        color: C.fg5, fontSize: '.66rem',
        textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
        marginBottom: 6,
      }}>Insert token</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {groups.map(g => {
          const ts = tokens.filter(t => t.group === g.id);
          if (!ts.length) return null;
          return (
            <div key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 64, flexShrink: 0,
                color: C.fg5, fontSize: '.68rem', fontWeight: 600,
              }}>{g.label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, flex: 1 }}>
                {ts.map(t => (
                  <button
                    key={t.token}
                    title={t.desc}
                    onClick={() => onInsert(t.token)}
                    style={{
                      background: C.input,
                      border: `1px solid ${C.borderStrong}`,
                      color: C.accentBright,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '.72rem', fontWeight: 600,
                      padding: '3px 7px',
                      borderRadius: 5,
                      cursor: 'pointer',
                    }}
                  >{t.token}</button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Numeric trio for the running-sequence range + next value.
function SequenceRangeRow({ start, end, next, onChange }) {
  // Clamp `next` if start/end change.
  const clamp = (n) => Math.min(Math.max(n, start), end);
  const update = (k, v) => {
    const num = Number(v);
    if (!Number.isFinite(num)) return;
    const out = { [k]: Math.round(num) };
    if (k === 'seqStart' && num > end) out.seqEnd = num;
    if (k === 'seqEnd' && num < start) out.seqStart = num;
    if (k !== 'seqNext') out.seqNext = clamp(next);
    onChange(out);
  };
  const remaining = Math.max(0, end - next + 1);

  return (
    <div style={{
      marginTop: 10,
      background: C.input,
      border: `1px dashed ${C.borderStrong}`,
      borderRadius: 8,
      padding: '10px 12px',
    }}>
      <div style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        marginBottom: 8,
      }}>
        <span style={{
          color: C.accentBright, fontSize: '.68rem', fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '.1em',
        }}>Sequence range</span>
        <span style={{
          color: C.fg5, fontSize: '.7rem',
          fontFamily: "'JetBrains Mono', monospace",
        }}>{remaining.toLocaleString()} numbers remaining</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
        <NumField label="Start" value={start} onChange={(v) => update('seqStart', v)} />
        <NumField label="End" value={end} onChange={(v) => update('seqEnd', v)} />
        <NumField label="Next" value={next} onChange={(v) => update('seqNext', v)} highlight />
      </div>
    </div>
  );
}

function NumField({ label, value, onChange, highlight }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{
        color: C.fg5, fontSize: '.64rem',
        textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700,
        marginBottom: 3,
      }}>{label}</div>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          width: '100%',
          background: highlight ? 'var(--info-bg)' : C.app,
          border: `1px solid ${highlight ? C.accentMuted : C.borderStrong}`,
          borderRadius: 6,
          padding: '6px 9px',
          color: highlight ? C.accentBright : C.fg1,
          fontFamily: "'JetBrains Mono', monospace",
          fontSize: '.82rem',
          fontWeight: 600,
          outline: 'none',
          boxSizing: 'border-box',
        }}
      />
    </label>
  );
}


// ── Debug tab ────────────────────────────────────────────────────────────
// Read-only diagnostics for the selected step, built entirely from data the
// app already has (runMap, step.config) — no new resolution logic, no
// backend calls of its own. Four panels: Resolved Source, Row Count In/Out,
// Warnings/Diagnostics, Raw Config.

const WARNING_ALERT_KIND = { error: 'error', warning: 'warn', info: 'info' };

function ResolvedSourcePanel({ step, info }) {
  const resolver = SOURCE_ANCHOR_RESOLVERS[step.type];
  const cfg = step.config || {};
  const selfCount = info?.inputCount;

  // Filter has no Source concept at all (always self) — a permanent design
  // choice, not a debugger gap like union below.
  if (step.type === 'filter') {
    return (
      <div>
        <Eyebrow style={{ marginBottom: 6 }}>Resolved Source</Eyebrow>
        <div style={{ color: C.fg2, fontSize: '.82rem', fontFamily: "'JetBrains Mono', monospace" }}>Self (always)</div>
        <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 6, lineHeight: 1.5 }}>
          Filter always reads the previous step's output — it has no Source field of its own.
        </div>
      </div>
    );
  }

  if (!resolver) {
    return (
      <div>
        <Eyebrow style={{ marginBottom: 6 }}>Resolved Source</Eyebrow>
        <div style={{ color: C.fg2, fontSize: '.82rem', fontFamily: "'JetBrains Mono', monospace" }}>
          {cfg.source ? `Source: ${cfg.source}` : 'Source: __self__ (default)'}
        </div>
        <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 6, lineHeight: 1.5 }}>
          Source resolution for this step type isn't available in the debugger yet.
        </div>
      </div>
    );
  }

  const anchor = resolver(cfg, selfCount ?? 0);
  let line;
  if (anchor && !(selfCount > 0)) {
    line = <>Self: <strong>empty (0 rows)</strong> — anchored to Source: <strong>{anchor}</strong></>;
  } else if (anchor) {
    line = <>Source: <strong>{anchor}</strong></>;
  } else if (selfCount == null) {
    line = <>Self: <span style={{ color: C.fg5 }}>unknown — run a preview to resolve</span></>;
  } else {
    line = <>Self: <strong>{selfCount}</strong> row{selfCount === 1 ? '' : 's'} (no anchor)</>;
  }

  return (
    <div>
      <Eyebrow style={{ marginBottom: 6 }}>Resolved Source</Eyebrow>
      <div style={{ color: C.fg2, fontSize: '.82rem', fontFamily: "'JetBrains Mono', monospace" }}>{line}</div>
    </div>
  );
}

function RowCountPanel({ info }) {
  if (!info) {
    return (
      <div>
        <Eyebrow style={{ marginBottom: 6 }}>Row Count In / Out</Eyebrow>
        <div style={{ color: C.fg5, fontSize: '.78rem' }}>No preview data yet for this step.</div>
      </div>
    );
  }
  return (
    <div>
      <Eyebrow style={{ marginBottom: 8 }}>Row Count In / Out</Eyebrow>
      <div style={{ display: 'flex', gap: 16 }}>
        <Stat label="Input" value={info.inputCount} />
        <Stat label="Output" value={info.outputCount} />
        <Stat label="Δ" value={(info.delta > 0 ? '+' : '') + info.delta}
          tone={info.delta < 0 ? 'warn' : info.delta > 0 ? 'ok' : 'neutral'} />
      </div>
    </div>
  );
}

function WarningsPanel({ info }) {
  const warnings = info?.warnings || [];
  return (
    <div>
      <Eyebrow style={{ marginBottom: 6 }}>Warnings / Diagnostics</Eyebrow>
      {warnings.length === 0 ? (
        <div style={{ color: C.fg5, fontSize: '.78rem' }}>No warnings on last run</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {warnings.map((w, i) => (
            <Alert key={i} kind={WARNING_ALERT_KIND[w.severity] || 'info'}>
              <div style={{ fontSize: '.8rem', lineHeight: 1.5 }}>
                {w.column && (
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, marginRight: 6 }}>
                    {w.column}
                  </span>
                )}
                {warningLabel(w.code) && (
                  <b style={{ marginRight: 6 }}>{warningLabel(w.code)}:</b>
                )}
                {w.message}
              </div>
              {w.suggested_fix && (
                <div style={{ marginTop: 4, fontSize: '.74rem', opacity: 0.85 }}>
                  Suggested fix: {w.suggested_fix}
                </div>
              )}
            </Alert>
          ))}
        </div>
      )}
    </div>
  );
}

// Per-step run statistics (info.stats, set by the step itself): branch counts
// as a small table, plus any other counters as label/value rows.
function CodeStatsPanel({ model }) {
  const cell = { padding: '4px 8px', fontSize: '.78rem', borderBottom: `1px solid ${C.border}`, verticalAlign: 'top' };
  const mono = { fontFamily: "'JetBrains Mono', monospace" };
  return (
    <div>
      <Eyebrow style={{ marginBottom: 6 }}>Run Stats · Code ({model.total} rows)</Eyebrow>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: C.input, border: `1px solid ${C.border}`, borderRadius: 8 }}>
        <thead>
          <tr style={{ color: C.fg4, fontSize: '.72rem', textAlign: 'left' }}>
            <th style={{ ...cell, fontWeight: 600 }}>Output</th>
            <th style={{ ...cell, fontWeight: 600, textAlign: 'right' }}>Blank</th>
            <th style={{ ...cell, fontWeight: 600, textAlign: 'right' }}>Kept</th>
            <th style={{ ...cell, fontWeight: 600 }}>Top values</th>
          </tr>
        </thead>
        <tbody>
          {model.outputs.map((o) => (
            <tr key={o.name}>
              <td style={{ ...cell, ...mono, color: C.fg1 }}>{o.name}</td>
              <td style={{ ...cell, ...mono, color: C.fg2, textAlign: 'right' }}>{o.blank}</td>
              <td style={{ ...cell, ...mono, color: C.fg2, textAlign: 'right' }}>{o.kept}</td>
              <td style={{ ...cell, color: C.fg3 }}>
                {o.top.length === 0 ? <span style={{ color: C.fg5 }}>none</span> : o.top.map((t, i) => (
                  <div key={i} style={mono}>{String(t.value)} <span style={{ color: C.fg5 }}>×{t.count}</span></div>
                ))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StatsPanel({ info }) {
  const stats = info?.stats;
  if (!stats) return null;
  const codeModel = mapCodeStats(stats);
  if (codeModel) return <CodeStatsPanel model={codeModel} />;
  const branches = Array.isArray(stats.branches) ? stats.branches : [];
  const scalars = Object.entries(stats).filter(([k, v]) => k !== 'branches' && typeof v === 'number');
  const label = (k) => { const t = k.replace(/_/g, ' '); return t.charAt(0).toUpperCase() + t.slice(1); };
  const cell = { padding: '4px 8px', fontSize: '.78rem', borderBottom: `1px solid ${C.border}` };
  return (
    <div>
      <Eyebrow style={{ marginBottom: 6 }}>Run Stats</Eyebrow>
      <table style={{ width: '100%', borderCollapse: 'collapse', background: C.input, border: `1px solid ${C.border}`, borderRadius: 8 }}>
        <tbody>
          {branches.map((b, i) => (
            <tr key={`b${i}`}>
              <td style={{ ...cell, color: C.fg2 }}>{b.label}</td>
              <td style={{ ...cell, color: C.fg1, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>{b.matched}</td>
            </tr>
          ))}
          {scalars.map(([k, v]) => (
            <tr key={k}>
              <td style={{ ...cell, color: C.fg4 }}>{label(k)}</td>
              <td style={{ ...cell, color: C.fg1, textAlign: 'right', fontFamily: "'JetBrains Mono', monospace" }}>{v}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RawConfigPanel({ step }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Eyebrow style={{ marginBottom: 0 }}>Raw Config</Eyebrow>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: C.accentBright, fontSize: '.72rem', fontWeight: 600,
            fontFamily: 'inherit', padding: 0,
          }}
        >{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && (
        <pre style={{
          marginTop: 8,
          background: C.input, border: `1px solid ${C.borderStrong}`, borderRadius: 8,
          padding: '10px 12px', fontFamily: "'JetBrains Mono', monospace",
          fontSize: '.74rem', color: C.fg2, lineHeight: 1.5,
          overflowX: 'auto', whiteSpace: 'pre',
        }}>{JSON.stringify(step.config || {}, null, 2)}</pre>
      )}
    </div>
  );
}

function DebugPanel({ step, runMap }) {
  const info = runMap?.get(step.id);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <ResolvedSourcePanel step={step} info={info} />
      <RowCountPanel info={info} />
      <StatsPanel info={info} />
      <WarningsPanel info={info} />
      <RawConfigPanel step={step} />
    </div>
  );
}

// ── Step inspector dispatcher (with shared chrome) ──────────────────────
export function StepInspector({ step, pipeline, runMap, testResult, onChange, onDelete, onTest, onReplaceInput, onChangeOutput, onBrowseFolder, onBrowseJoinFile, secondaryDatasets, sheets, onToast, onClearTest, onCollapse, onRefreshSchema, onDone, columns = SOURCE_COLUMNS, originalColumns, uploadedRows }) {
  // Configuration/Debug tab, local to this panel. Resets to Configuration on
  // every step change (not just a step-type change) — a selected step's own
  // in-progress config edits live in `step`/`onChange`, never in this state,
  // so resetting here never discards anything. Declared above the `!step`
  // early return so hook order stays fixed across renders either way.
  const [panelTab, setPanelTab] = React.useState('config');
  React.useEffect(() => { setPanelTab('config'); }, [step?.id]);

  if (!step) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{
          padding: '12px 14px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <Eyebrow style={{ flex: 1 }}>Inspector</Eyebrow>
          {onCollapse && <IconButton title="Hide inspector (⌥3)" onClick={onCollapse}>›</IconButton>}
        </div>
        <div style={{ padding: '40px 20px', textAlign: 'center', color: C.fg5 }}>
          <div style={{ fontSize: 36, marginBottom: 10, opacity: 0.5 }}>◌</div>
          <div style={{ color: C.fg3, fontSize: '.86rem', marginBottom: 4, fontWeight: 600 }}>Nothing selected</div>
          <div style={{ fontSize: '.78rem', lineHeight: 1.5 }}>Click any node in the canvas to inspect or edit its configuration.</div>
        </div>
      </div>
    );
  }

  const meta = STEP_TYPES[step.type] || {};
  const accent = stepAccent(step);
  const cat = CATEGORIES[meta.cat] || {};
  const info = runMap?.get(step.id);
  const isTerminal = step.type === 'source' || step.type.startsWith('source_') || step.type === 'output';
  // The Output step passes rows straight through, so "previous step → Output"
  // says nothing. For it the header compares the pipeline's own input (the
  // Source step's rows) with the final output instead.
  const sourceStep = step.type === 'output'
    ? (pipeline?.steps || []).find(s => s.type === 'source' || s.type.startsWith('source_'))
    : null;
  const sourceCount = sourceStep ? runMap?.get(sourceStep.id)?.outputCount : undefined;
  const headerStats = !info ? null
    : step.type === 'output' && sourceCount != null
      ? { inLabel: 'Source', outLabel: 'Output', in: sourceCount, out: info.outputCount, delta: info.outputCount - sourceCount }
      : { inLabel: 'Input', outLabel: 'Output', in: info.inputCount, out: info.outputCount, delta: info.delta };

  // Dispatcher: lookup a specific inspector, otherwise generic placeholder
  const Specific =
    step.type === 'source' || step.type === 'source_url' || step.type === 'source_cloud' ? SourceInspector :
      step.type === 'dedupe' ? DedupeInspector :
        step.type === 'filter' ? FilterInspector :
          step.type === 'output' ? OutputInspector :
            INSPECTORS[step.type] || PlaceholderInspector;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
      <div style={{
        padding: '14px 16px 12px',
        borderBottom: `1px solid ${C.border}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div style={{
            width: 32, height: 32, borderRadius: 8,
            background: C.input, border: `1px solid ${accent}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: accent, flexShrink: 0,
          }}><StepIcon name={meta.icon} size={17} /></div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <Eyebrow style={{ color: accent }}>{cat.label || meta.label} · {meta.label}</Eyebrow>
            <div style={{
              color: C.fg1, fontSize: '.86rem', fontWeight: 600, marginTop: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{summarizeStep(step)}</div>
          </div>
          {!isTerminal && (
            <IconButton title="Delete step" onClick={() => onDelete(step.id)} style={{ flexShrink: 0 }}><Trash2 size={14} /></IconButton>
          )}
          {onCollapse && (
            <IconButton title="Hide inspector (⌥3)" onClick={onCollapse} style={{ flexShrink: 0 }}>›</IconButton>
          )}
        </div>

        {info && (
          <div style={{
            display: 'flex', gap: 16, marginTop: 12, alignItems: 'center',
            padding: '8px 10px',
            background: C.input, border: `1px solid ${C.border}`, borderRadius: 8,
          }}>
            <Stat label={headerStats.inLabel} value={headerStats.in} />
            <Stat label={headerStats.outLabel} value={headerStats.out} />
            <Stat label="Δ" value={(headerStats.delta > 0 ? '+' : '') + headerStats.delta}
              tone={headerStats.delta < 0 ? 'warn' : headerStats.delta > 0 ? 'ok' : 'neutral'} />
            {info.warnings?.length > 0 && (
              <WarningBadge count={info.warnings.length} onClick={() => setPanelTab('debug')} />
            )}
          </div>
        )}

        {testResult && (
          <TestResultCard
            result={testResult}
            stepLabel={step.label || summarizeStep(step)}
            onDismiss={onClearTest}
          />
        )}
      </div>

      <div style={{ display: 'flex', borderBottom: `1px solid ${C.border}`, padding: '0 16px' }}>
        {[['config', 'Configuration'], ['debug', 'Debug']].map(([k, l]) => (
          <button key={k} onClick={() => setPanelTab(k)}
            style={{
              flex: 1, background: 'transparent',
              border: 'none', borderBottom: `2px solid ${panelTab === k ? C.accent : 'transparent'}`,
              color: panelTab === k ? C.fg1 : C.fg4,
              fontFamily: 'inherit', fontWeight: panelTab === k ? 600 : 500,
              fontSize: '.78rem', padding: '8px 6px', cursor: 'pointer',
              marginBottom: -1, transition: 'all .15s',
            }}>{l}</button>
        ))}
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px' }}>
        {/* `<Specific>` stays mounted under both tabs (hidden via `display`,
            not unmounted) — several inspectors (e.g. RenameInspector) hold
            uncommitted local draft state that a Configuration/Debug toggle
            must never discard; conditionally rendering it would remount it
            and silently lose that state every time. */}
        <div className="dp-config-cols" style={{ display: panelTab === 'debug' ? 'none' : 'block' }}>
          {/* Shared optional gate, a sibling of the block's own inspector —
              shown only for GATEABLE_STEPS, and ABOVE the block's own
              settings because it is evaluated first (rows that fail it skip
              the step). `columns` is this step's INPUT schema, which is what
              the gate is evaluated against. */}
          {GATEABLE_STEPS[step.type] && (
            <ApplyWhenSection
              step={step} onChange={onChange} columns={columns}
              secondaryDatasets={secondaryDatasets} sheets={sheets} pipeline={pipeline}
              runMap={runMap} onBrowseJoinFile={onBrowseJoinFile} uploadedRows={uploadedRows}
            />
          )}
          <ColumnsContext.Provider value={columns}>
            <OriginalColumnsContext.Provider value={originalColumns || columns}>
              <Specific step={step} pipeline={pipeline} runMap={runMap} onChange={onChange} onReplaceInput={onReplaceInput} onChangeOutput={onChangeOutput} onBrowseFolder={onBrowseFolder} onBrowseJoinFile={onBrowseJoinFile} secondaryDatasets={secondaryDatasets} sheets={sheets} onToast={onToast} columns={columns} uploadedRows={uploadedRows} />
            </OriginalColumnsContext.Provider>
          </ColumnsContext.Provider>
        </div>
        {panelTab === 'debug' && <DebugPanel step={step} runMap={runMap} />}
      </div>

      <div style={{
        padding: 12, borderTop: `1px solid ${C.border}`,
        background: C.bar, display: 'flex', gap: 8,
      }}>
        <Button
          kind="primary"
          size="sm"
          icon={<Check size={14} />}
          onClick={() => {
            onToast?.({
              kind: 'ok',
              title: 'Changes applied',
              detail: step.label || summarizeStep(step),
            });
            onDone?.();
          }}
        >Done</Button>
        {!isTerminal && (
          testResult?.status === 'running' ? (
            <Button kind="accent" disabled icon={<Spinner />}>Testing…</Button>
          ) : testResult?.status === 'done' ? (
            <Button kind="accent" icon={<RotateCcw size={14} />} onClick={() => onTest?.(step)}>Re-test step</Button>
          ) : testResult?.status === 'error' ? (
            <Button kind="danger" icon={<RotateCcw size={14} />} onClick={() => onTest?.(step)}>Retry test</Button>
          ) : (
            <Button kind="accent" icon="▶" onClick={() => onTest?.(step)}>Test this step</Button>
          )
        )}
        <div style={{ flex: 1 }} />
        {!isTerminal && (
          <Button
            kind="ghost"
            size="sm"
            title={step.disabled ? 'Include this step in runs again' : 'Skip this step — rows pass through unchanged'}
            onClick={() => onChange({ ...step, disabled: !step.disabled })}
          >{step.disabled ? 'Enable' : 'Disable'}</Button>
        )}
        {step.type === 'source' && (
          <Button
            kind="ghost"
            size="sm"
            icon={<RotateCcw size={14} />}
            title="Re-read the columns already stored on the server for this pipeline — not the file on your computer. If you edited the file on disk, use 'Replace…'/'Change…' above instead."
            onClick={() => onRefreshSchema?.()}
          >Refresh schema</Button>
        )}
      </div>
    </div>
  );
}

// Warning-count pill next to the Input/Output/Δ stats — click jumps to the
// Debug tab's existing Warnings panel. Rendered only when info.warnings has
// at least one entry (see the call site); shows nothing otherwise.
function WarningBadge({ count, onClick }) {
  return (
    <button
      onClick={onClick}
      title={`${count} warning${count === 1 ? '' : 's'} on last run — click to view`}
      style={{
        marginLeft: 'auto',
        display: 'flex', alignItems: 'center', gap: 4,
        background: 'var(--warn-bg)', border: '1px solid var(--warn-border)',
        borderRadius: 999, padding: '3px 8px',
        color: C.warn, fontFamily: 'inherit', fontSize: '.72rem', fontWeight: 700,
        cursor: 'pointer',
      }}
    >
      <span aria-hidden style={{ display: 'inline-flex' }}><AlertTriangle size={12} /></span>{count}
    </button>
  );
}

// Tiny stat block used in inspector header
function Stat({ label, value, tone = 'neutral' }) {
  const c = tone === 'warn' ? C.warn : tone === 'ok' ? C.ok : C.fg1;
  return (
    <div>
      <div style={{ color: C.fg5, fontSize: '.66rem', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700 }}>{label}</div>
      <div style={{ color: c, fontFamily: "'JetBrains Mono', monospace", fontSize: '.95rem', fontWeight: 600, marginTop: 2 }}>{value}</div>
    </div>
  );
}

// ── Spinner used by the "Testing…" button state ────────────────────────
export function Spinner({ size = 12, color }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: size, height: size,
        border: `2px solid ${color || C.accentMuted}`,
        borderTopColor: color ? '#fff' : C.accentBright,
        borderRadius: '50%',
        animation: 'dp-spin .8s linear infinite',
      }}
    />
  );
}

// ── Test result card (shown in the inspector header after a test) ──────
export function TestResultCard({ result, stepLabel, onDismiss }) {
  // "7s ago" relative timestamp, re-rendered every 10s.
  const [, force] = React.useReducer(x => x + 1, 0);
  React.useEffect(() => {
    if (result.status === 'running') return;
    const id = setInterval(force, 10000);
    return () => clearInterval(id);
  }, [result.status, result.finishedAt]);

  if (result.status === 'running') {
    return (
      <div style={{
        marginTop: 10, padding: '10px 12px',
        background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
        borderRadius: 8,
        display: 'flex', alignItems: 'center', gap: 10,
        animation: 'dp-pulse 1.2s ease-in-out infinite',
      }}>
        <Spinner />
        <span style={{ color: C.accentBright, fontWeight: 600, fontSize: '.78rem' }}>
          Testing step…
        </span>
        <span style={{ color: C.fg5, fontSize: '.72rem' }}>
          executing against upstream rows
        </span>
      </div>
    );
  }

  if (result.status === 'error') {
    return (
      <div style={{
        marginTop: 10, padding: '10px 12px',
        background: 'var(--error-bg)', border: `1px solid #7f1d1d`,
        borderRadius: 8,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ color: C.error, display: 'inline-flex' }}><XCircle size={15} /></span>
          <span style={{ color: C.error, fontWeight: 700, fontSize: '.78rem' }}>
            Test failed
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={onDismiss} style={dismissBtn} title="Clear result"><X size={13} /></button>
        </div>
        <div style={{
          color: C.fg3, fontSize: '.74rem', marginTop: 6,
          fontFamily: "'JetBrains Mono', monospace", lineHeight: 1.5,
        }}>{result.error}</div>
      </div>
    );
  }

  const { durationMs, inputCount, outputCount, delta,
    rowsDropped, rowsAdded, rowsChanged, cellsChanged,
    changeSamples = [], finishedAt } = result;
  const noEffect = delta === 0 && cellsChanged === 0;
  const ago = relTime(finishedAt);

  // Pick a single headline phrase summarising the dominant change.
  let headline;
  if (noEffect) headline = 'No rows or cells changed';
  else if (delta < 0) headline = `Dropped ${Math.abs(delta)} row${Math.abs(delta) === 1 ? '' : 's'}`;
  else if (delta > 0) headline = `Added ${delta} row${delta === 1 ? '' : 's'}`;
  else if (cellsChanged > 0) headline = `Updated ${cellsChanged} cell${cellsChanged === 1 ? '' : 's'} across ${rowsChanged} row${rowsChanged === 1 ? '' : 's'}`;
  else headline = 'Step ran';

  return (
    <div style={{
      marginTop: 10, padding: '10px 12px',
      background: noEffect ? C.input : 'var(--ok-bg)',
      border: `1px solid ${noEffect ? C.borderStrong : 'var(--ok-border)'}`,
      borderRadius: 8,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{
          color: noEffect ? C.fg4 : C.ok,
          display: 'inline-flex',
        }}>{noEffect ? <Circle size={14} /> : <CheckCircle2 size={14} />}</span>
        <span style={{
          color: noEffect ? C.fg2 : C.ok,
          fontWeight: 700, fontSize: '.78rem',
        }}>{headline}</span>
        <div style={{ flex: 1 }} />
        <span style={{
          color: C.fg5, fontSize: '.7rem',
          fontFamily: "'JetBrains Mono', monospace",
        }}>{durationMs} ms · {ago}</span>
        <button onClick={onDismiss} style={dismissBtn} title="Clear result"><X size={13} /></button>
      </div>

      {/* Detail row: rows in/out/dropped/added/cells */}
      <div style={{
        display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8,
      }}>
        <ResultChip label="in" value={inputCount} />
        <ResultChip label="out" value={outputCount} tone={delta !== 0 ? (delta < 0 ? 'warn' : 'ok') : 'neutral'} />
        {rowsDropped > 0 && <ResultChip label="dropped" value={rowsDropped} tone="warn" />}
        {rowsAdded > 0 && <ResultChip label="added" value={rowsAdded} tone="ok" />}
        {cellsChanged > 0 && <ResultChip label="cells changed" value={cellsChanged} tone="accent" />}
        {rowsChanged > 0 && delta === 0 && cellsChanged === 0 && <ResultChip label="rows changed" value={rowsChanged} tone="accent" />}
      </div>

      {changeSamples.length > 0 && (
        <div style={{
          marginTop: 8, paddingTop: 8,
          borderTop: `1px dashed ${C.border}`,
          fontSize: '.72rem', color: C.fg4, lineHeight: 1.6,
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          {changeSamples.map((s, i) => (
            <div key={i} style={{
              display: 'flex', gap: 6, alignItems: 'baseline',
              overflow: 'hidden', whiteSpace: 'nowrap',
            }}>
              <span style={{ color: C.fg6, flexShrink: 0 }}>#{s.id}</span>
              <span style={{ color: C.accentBright, flexShrink: 0 }}>{s.column}</span>
              <span style={{
                color: C.fg5, textDecoration: 'line-through',
                overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 88,
              }}>{s.from === '' || s.from == null ? '∅' : String(s.from)}</span>
              <span style={{ color: C.fg5 }}>→</span>
              <span style={{
                color: C.ok,
                overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 120,
              }}>{s.to === '' || s.to == null ? '∅' : String(s.to)}</span>
            </div>
          ))}
          {rowsChanged > changeSamples.length && (
            <div style={{ color: C.fg6, marginTop: 2 }}>
              … and {rowsChanged - changeSamples.length} more row{rowsChanged - changeSamples.length === 1 ? '' : 's'} changed
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ResultChip({ label, value, tone = 'neutral' }) {
  const palette = {
    neutral: { bg: C.input, bd: C.border, fg: C.fg2, accent: C.fg5 },
    ok: { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok, accent: '#34d39988' },
    warn: { bg: 'var(--warn-bg)', bd: 'var(--warn-border)', fg: C.warn, accent: '#fbbf2488' },
    accent: { bg: 'var(--info-bg)', bd: C.accentMuted, fg: C.accentBright, accent: C.accentMuted },
  }[tone] || {};
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'baseline', gap: 5,
      padding: '3px 8px',
      background: palette.bg, border: `1px solid ${palette.bd}`,
      borderRadius: 6,
      fontSize: '.7rem', fontWeight: 600,
      fontFamily: 'inherit',
    }}>
      <span style={{
        color: palette.fg,
        fontFamily: "'JetBrains Mono', monospace",
        fontSize: '.78rem',
      }}>{value}</span>
      <span style={{
        color: palette.accent,
        textTransform: 'uppercase', letterSpacing: '.06em',
        fontSize: '.62rem', fontWeight: 700,
      }}>{label}</span>
    </span>
  );
}

const dismissBtn = {
  background: 'transparent', border: 'none',
  color: C.fg5, cursor: 'pointer',
  width: 18, height: 18, padding: 0,
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  fontFamily: 'inherit',
};

function relTime(ts) {
  if (!ts) return 'just now';
  const s = Math.round((Date.now() - ts) / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 600) return `${Math.floor(s / 60)}m ago`;
  return 'a while ago';
}
